import React, { useEffect, useState } from "react";
import { Modal } from 'antd';
import { SmileOutlined, FrownOutlined } from '@ant-design/icons';

import {
    Spin, message, List, Card, Typography, Divider, Tooltip, Button
} from "antd";
import {
    LinkOutlined,
    CheckCircleFilled,
    GiftFilled
} from "@ant-design/icons";
import { motion } from "framer-motion";
import taskApi from "../../api/taskApi";
import getIcon from "../../utils/Icon";
import issue from '../../api/awardApi'
import awardApi from "../../api/awardApi";
const { Title, Text } = Typography;

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
        opacity: 1,
        y: 0,
        transition: {
            delay: i * 0.05,
            duration: 0.4,
        },
    }),
};

export default function UserTaskProgress({ promoId, userId }) {
    const [completedTasks, setCompletedTasks] = useState([]);
    const [uncompletedTasks, setUncompletedTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [updatingId, setUpdatingId] = useState(null);
    const [claiming, setClaiming] = useState(false);
    const [rewardSuccess, setRewardSuccess] = useState(false);
    const [rewardError, setRewardError] = useState({ visible: false, message: '' });

    const isValidURL = (str) => {
        try { new URL(str); return true; } catch { return false; }
    };

    const getTaskIconKey = (details, title) => {
        if (details?.includes('youtube.com')) return 'youtube';
        if (details?.includes('telegram')) return 'telegram';
        return 'polygon';
    };

    const fetchProgress = async () => {
        try {
            setLoading(true);
            const data = await taskApi.getUserProgress(promoId, userId);
            setCompletedTasks(data.completed_tasks || []);
            setUncompletedTasks(data.uncompleted_tasks || []);
        } catch (e) {
            console.error(e);
            message.error("Не удалось загрузить прогресс задач");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (promoId && userId) fetchProgress();
    }, [promoId, userId]);

    const handleComplete = async (taskId) => {
        setUpdatingId(taskId);
        try {
            await taskApi.completeTask({ userId, promoId, taskId });
            message.success("Задача выполнена");
            const task = uncompletedTasks.find(t => t.id === taskId);
            setUncompletedTasks(prev => prev.filter(t => t.id !== taskId));
            setCompletedTasks(prev => [{ ...task, completed_at: new Date().toISOString() }, ...prev]);
        } catch (e) {
            console.error(e);
            message.error("Ошибка при завершении задачи");
        } finally {
            setUpdatingId(null);
        }
    };

    const handleClaim = async () => {
        setClaiming(true);
        try {
            const response = await awardApi.issue({ promoId, userId });
            console.log(response);
            setRewardSuccess(true); // показываем модалку
        } catch (e) {
            console.error(e);
            let msg = 'Ошибка при получении награды';
            if (e?.response?.data?.message) {
                msg = e.response.data.message;
            }
            setRewardError({ visible: true, message: msg });
        } finally {
            setClaiming(false);
        }
    };


    const total = completedTasks.length + uncompletedTasks.length;
    const percent = total > 0 ? Math.round((completedTasks.length / total) * 100) : 0;

    if (loading) {
        return <div className="flex justify-center items-center h-32"><Spin size="large" /></div>;
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
        >
            <Card className="bg-gray-900 text-white border-gray-700 shadow-md rounded-xl">
                <div className="mb-6">
                    <div className="relative w-full h-4 bg-gray-700 rounded-full overflow-hidden">
                        <div
                            className="absolute top-0 left-0 h-full transition-all duration-700"
                            style={{
                                width: `${percent}%`,
                                background: 'linear-gradient(90deg, #34d399 0%, #10b981 100%)'
                            }}
                        />
                    </div>
                    <div className="text-sm text-gray-300 mt-2">
                        Завершено: {completedTasks.length} из {total} задач ({percent}%)
                    </div>
                </div>

                {uncompletedTasks.length === 0 && total > 0 && (
                    <div className="flex justify-center mb-4">
                        <Button
                            type="primary"
                            icon={<GiftFilled />}
                            loading={claiming}
                            onClick={handleClaim}
                            className="bg-yellow-500 hover:bg-orange-400 border-none"
                        >
                            Claim
                        </Button>
                    </div>
                )}

                <Divider className="bg-gray-600" />
                <Text className="text-white block mb-2">Невыполненные ({uncompletedTasks.length})</Text>
                <List
                    dataSource={uncompletedTasks}
                    locale={{ emptyText: <Text type="secondary">Нет невыполненных задач</Text> }}
                    renderItem={(item, i) => {
                        const iconKey = getTaskIconKey(item.details, item.title);
                        return (
                            <motion.div
                                key={item.id}
                                custom={i}
                                initial="hidden"
                                animate="visible"
                                variants={itemVariants}
                            >
                                <List.Item className="flex justify-between items-center mb-2 pb-2 border-b border-gray-800">
                                    <div className="flex items-center gap-3">
                                        <img src={getIcon(iconKey)} alt="icon" className="w-5 h-5" />
                                        <Text className="text-white">{item.title}</Text>
                                        {isValidURL(item.details) && (
                                            <Tooltip title="Открыть задачу">
                                                <a
                                                    href={item.details}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-400 hover:text-blue-500 ml-2"
                                                >
                                                    <LinkOutlined />
                                                </a>
                                            </Tooltip>
                                        )}
                                    </div>
                                    <Button
                                        type="link"
                                        loading={updatingId === item.id}
                                        onClick={() => handleComplete(item.id)}
                                        icon={<CheckCircleFilled className="text-green-500 text-lg" />}
                                        className="hover:text-green-400"
                                    />
                                </List.Item>
                            </motion.div>
                        );
                    }}
                />

                <Divider className="bg-gray-600 mt-6" />
                <Text className="text-white block mb-2">Выполненные ({completedTasks.length})</Text>
                <List
                    dataSource={completedTasks}
                    locale={{ emptyText: <Text type="secondary">Нет выполненных задач</Text> }}
                    renderItem={(item, i) => {
                        const iconKey = getTaskIconKey(item.details, item.title);
                        return (
                            <motion.div
                                key={item.id}
                                custom={i}
                                initial="hidden"
                                animate="visible"
                                variants={itemVariants}
                            >
                                <List.Item className="flex justify-between items-center bg-green-700/10 rounded-lg px-2 py-2 mb-2">
                                    <div className="flex items-center gap-3">
                                        <CheckCircleFilled className="text-green-500" />
                                        <Text className="text-gray-300 line-through">{item.title}</Text>
                                        {isValidURL(item.details) && (
                                            <Tooltip title="Открыть задачу">
                                                <a
                                                    href={item.details}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-400 hover:text-blue-500 ml-2"
                                                >
                                                    <LinkOutlined />
                                                </a>
                                            </Tooltip>
                                        )}
                                    </div>
                                    <div className="text-xs text-gray-400">
                                        {new Date(item.completed_at || item.created_at).toLocaleDateString()}
                                    </div>
                                </List.Item>
                            </motion.div>
                        );
                    }}
                />
            </Card>
            <Modal
                open={rewardSuccess}
                centered
                footer={null}
                onCancel={() => setRewardSuccess(false)}
            >
                <div className="text-center py-6">
                    <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: "spring", stiffness: 260, damping: 20 }}
                    >
                        <GiftFilled className="text-yellow-500 text-6xl mb-4" />
                    </motion.div>
                    <Title level={3}>Поздравляем!</Title>
                    <Text>Вы успешно получили награду 🎉</Text>
                </div>
            </Modal>

            <Modal
                open={rewardError.visible}
                centered
                onCancel={() => setRewardError({ visible: false, message: '' })}
                footer={null}
            >
                <div className="text-center py-6">
                    <motion.div
                        initial={{ rotate: 0 }}
                        animate={{ rotate: 10 }}
                        transition={{ type: "spring", stiffness: 100 }}
                    >
                        <FrownOutlined className="text-red-500 text-6xl mb-4" />
                    </motion.div>
                    <Title level={4}>Невозможно выдать награду</Title>
                    <Text type="danger">{rewardError.message}</Text>
                </div>
            </Modal>

        </motion.div>
    );
}
